import type { components } from '@nutians/api-client';
import { LOG_INPUT_SOFT_LIMIT_CHARS } from '@nutians/shared';
/**
 * LogPanel — the primary daily logging surface (J2).
 * T11.3 additions: QuickLogStrip (favourites + recents), Quick-add sheet,
 * Save-as-meal dialog, Copy-yesterday action.
 *
 * Responsibilities:
 *  1. Free-text log input -> POST /me/logs -> chips + clarification rows.
 *  2. Renders EntryChip for each persisted entry (inline edit, delete).
 *  3. Renders DisambiguationRow for unresolved clarifications.
 *  4. Keeps DayTotalsBar in sync via the echoed DaySummary (FR-4.3).
 *  5. Shows a 5-second UndoToast on entry delete.
 *  6. Reads ?prefill= + ?alertId= from URL on mount (email / web-push click-through,
 *     T4.3 J3 step 3). Fires alert_quick_add_used analytics on submit.
 *
 * TanStack Query: logMutation uses the raw api client; the day totals are
 * stored as local React state (updated optimistically from each response).
 * Query cache invalidation happens inside EntryChip/DisambiguationRow.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.js';
import { DAY_ENTRIES_KEY, useDayEntries } from '../hooks/useDayEntries.js';
import { useEntitlement } from '../hooks/useEntitlement.js';
import { RECENTS_KEY } from '../hooks/useRecents.js';
import { fileToVisionImage, type VisionImage } from '../utils/image.js';
import { DayTotalsBar } from './DayTotalsBar.js';
import { DisambiguationRow } from './DisambiguationRow.js';
import { EntryChip } from './EntryChip.js';
import { LogHistoryPanel } from './LogHistoryPanel.js';
import { MicroPanel } from './MicroPanel.js';
import { QuickAddSheet } from './QuickAddSheet.js';
import { QuickLogStrip } from './QuickLogStrip.js';
import { SaveMealDialog } from './SaveMealDialog.js';
import { UndoToast } from './UndoToast.js';

type LogResponse = components['schemas']['LogResponse'];
type MealEntryView = components['schemas']['MealEntryView'];
type DaySummary = components['schemas']['DaySummary'];

// ---- Undo queue entry --------------------------------------------------------

interface UndoItem {
  id: string;
  label: string;
  restoreFn: () => void;
}

// ---- Component ---------------------------------------------------------------

export function LogPanel() {
  const { t } = useTranslation('log');
  const qc = useQueryClient();
  const { data: entitlement } = useEntitlement();
  const today = new Date().toISOString().slice(0, 10);
  const { data: dayEntriesData } = useDayEntries(today);
  const [inputText, setInputText] = useState('');
  const [logResult, setLogResult] = useState<LogResponse | null>(null);
  const [day, setDay] = useState<DaySummary | null>(null);
  const [undoItem, setUndoItem] = useState<UndoItem | null>(null);
  const [pendingAlertId, setPendingAlertId] = useState<string | null>(null);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showSaveMeal, setShowSaveMeal] = useState(false);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const [pendingImage, setPendingImage] = useState<VisionImage | null>(null);
  const [imageCapture, setImageCapture] = useState<'meal' | 'label'>('meal');
  const [imageError, setImageError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Read ?prefill= + ?alertId= from URL on mount (email / web-push click-through, T4.3 J3).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const prefillParam = params.get('prefill');
    const alertIdParam = params.get('alertId');
    if (prefillParam) {
      setInputText(decodeURIComponent(prefillParam));
      if (alertIdParam) setPendingAlertId(alertIdParam);
      // Clean URL so a page reload doesn't re-prefill.
      const clean = new URL(window.location.href);
      clean.searchParams.delete('prefill');
      clean.searchParams.delete('alertId');
      window.history.replaceState(null, '', clean.toString());
    }
  }, []);

  // ---- Log submission --------------------------------------------------------

  const handleLogSuccess = useCallback(
    (data: LogResponse) => {
      setLogResult(data);
      setDay(data.day);
      setInputText('');
      void qc.invalidateQueries({ queryKey: DAY_ENTRIES_KEY(today) });
      // Analytics: if a quick-add from an alert was submitted, stamp action_taken_at.
      if (pendingAlertId) {
        void api.POST('/me/notifications/{notificationId}/quick-add-used', {
          params: { path: { notificationId: pendingAlertId } },
        });
        setPendingAlertId(null);
      }
    },
    [qc, today, pendingAlertId],
  );

  const logMutation = useMutation({
    mutationFn: async (text: string) => {
      const { data, error } = await api.POST('/me/logs', {
        body: {
          text,
          clientNow: new Date().toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
      if (error || !data) throw new Error('Log submission failed');
      return data;
    },
    onSuccess: handleLogSuccess,
  });

  const imageMutation = useMutation({
    mutationFn: async (image: VisionImage) => {
      const { data, error } = await api.POST('/me/logs', {
        body: {
          text: '',
          imageBase64: image.base64,
          imageMediaType: image.mediaType,
          imageCapture,
          clientNow: new Date().toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
      if (error || !data) throw new Error('Image log failed');
      return data;
    },
    onSuccess: (data) => {
      handleLogSuccess(data);
      setPendingImage(null);
      setImageCapture('meal');
    },
  });

  const copyYesterdayMutation = useMutation({
    mutationFn: async () => {
      const yesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
      const { data, error } = await api.POST('/me/logs/copy', {
        body: { sourceDate: yesterday, targetDate: today, scope: 'day' },
      });
      if (error || !data) throw new Error('Failed to copy');
      return { data, yesterday };
    },
    onSuccess: ({ data, yesterday }) => {
      setDay(data.day);
      void qc.invalidateQueries({ queryKey: RECENTS_KEY });
      if (data.copiedCount === 0) {
        setCopyStatus(t('quickLog.copiedNone', { date: yesterday }));
      } else {
        setCopyStatus(t('quickLog.copied', { count: data.copiedCount, date: yesterday }));
      }
      setTimeout(() => setCopyStatus(null), 4000);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = inputText.trim();
    if (!trimmed || logMutation.isPending) return;
    logMutation.mutate(trimmed);
  }

  async function handlePickImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setImageError(null);
    try {
      setPendingImage(await fileToVisionImage(file));
    } catch {
      setImageError(t('photo.error'));
    }
  }

  // Keyboard shortcut: Cmd/Ctrl+Enter submits.
  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      const trimmed = inputText.trim();
      if (trimmed && !logMutation.isPending) logMutation.mutate(trimmed);
    }
  }

  // ---- Entry updates from chips / clarification rows -------------------------

  const handleDayUpdate = useCallback((updated: DaySummary) => {
    setDay(updated);
  }, []);

  const handleDeleteRequest = useCallback((deleted: MealEntryView, restoreFn: () => void) => {
    // Remove the entry from the local result optimistically.
    setLogResult((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        entries: prev.entries.filter((e) => e.id !== deleted.id),
      };
    });

    setUndoItem({
      id: deleted.id,
      label: `"${deleted.name}" removed`,
      restoreFn,
    });
  }, []);

  const handleUndoToastDismiss = useCallback(() => setUndoItem(null), []);

  const handleUndo = useCallback(() => {
    undoItem?.restoreFn();
    // Re-add entry to local list (server response will supply authoritative version
    // via the invalidated query; for now just clear undo state).
    setUndoItem(null);
  }, [undoItem]);

  // ---- Clarification resolution ----------------------------------------------

  const handleClarificationResolved = useCallback((freshResponse: LogResponse) => {
    setLogResult(freshResponse);
    setDay(freshResponse.day);
  }, []);

  // ---- Duplicate-entry merge -------------------------------------------------

  const handleMergeRequest = useCallback(
    async (entry: MealEntryView) => {
      if (!logResult) return;
      // All entries in logResult come from the same raw_log submission.
      const sibling = logResult.entries.find(
        (e) =>
          e.id !== entry.id &&
          e.foodId !== null &&
          e.foodId === entry.foodId &&
          e.deletedAt === null,
      );
      if (!sibling) return;

      const totalG = (entry.quantityG ?? 0) + (sibling.quantityG ?? 0);

      const { data: patchData, error: patchError } = await api.PATCH('/me/entries/{id}', {
        params: { path: { id: entry.id } },
        body: { displayQuantity: totalG, displayUnit: 'g' },
      });
      if (patchError || !patchData) return;

      const { data: delData, error: delError } = await api.DELETE('/me/entries/{id}', {
        params: { path: { id: sibling.id } },
      });
      if (delError || !delData) return;

      setLogResult((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          entries: prev.entries
            .filter((e) => e.id !== sibling.id)
            .map((e) =>
              e.id === entry.id ? { ...e, displayQuantity: totalG, displayUnit: 'g' } : e,
            ),
        };
      });
      setDay(delData.day);
      const date = new Date(entry.recordedAt).toISOString().slice(0, 10);
      void qc.invalidateQueries({ queryKey: ['days', date] });
    },
    [logResult, qc],
  );

  // ---- Focus management ------------------------------------------------------

  // Keep focus in the textarea while results render.
  useEffect(() => {
    if (!logMutation.isPending && logMutation.isSuccess) {
      inputRef.current?.focus();
    }
  }, [logMutation.isPending, logMutation.isSuccess]);

  // ---- Render ----------------------------------------------------------------

  const hasEntries = logResult && logResult.entries.length > 0;
  const hasClarifications = logResult && logResult.clarifications.length > 0;

  return (
    <section aria-label="Daily logging" className="flex flex-col gap-4">
      {/* Day totals bar — updates optimistically on every mutation */}
      {day && <DayTotalsBar day={day} />}

      {/* Micronutrient panel — free: basic 4; premium: full 10 + gap tips */}
      {day && (
        <MicroPanel micros={day.micros ?? null} isPremium={entitlement?.isPremium ?? false} />
      )}

      {/* Log input */}
      <div className="flex flex-col gap-2">
        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
          <label htmlFor="log-input" className="text-sm font-medium text-ink dark:text-ink">
            {t('label')}
          </label>
          <textarea
            id="log-input"
            ref={inputRef}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={logMutation.isPending}
            rows={2}
            placeholder={t('placeholder')}
            aria-label="Log food in natural language"
            aria-describedby="log-input-hint"
            className="
              w-full resize-none rounded-lg border border-ink/20 px-4 py-3
              text-sm placeholder-neutral-400
              focus:outline-none focus:ring-2 focus:ring-leaf
              disabled:opacity-50
              dark:border-ink/20 dark:bg-shell dark:text-ink
              dark:placeholder-neutral-500
            "
          />
          <p id="log-input-hint" className="text-xs text-ink-soft dark:text-ink-soft">
            Press{' '}
            <kbd className="rounded border border-ink/20 px-1 dark:border-ink/20">⌘ Enter</kbd>{' '}
            {t('hint')}
          </p>
          {inputText.trim().length > LOG_INPUT_SOFT_LIMIT_CHARS && (
            <p aria-live="polite" className="text-xs text-acorn dark:text-acorn">
              {t('tooLong')}
            </p>
          )}
          <div className="flex items-center justify-end gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePickImage}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={imageMutation.isPending}
              className="
                rounded-lg border border-ink/20 px-4 py-2 text-sm font-medium
                text-ink hover:bg-husk/40
                focus:outline-none focus:ring-2 focus:ring-leaf
                disabled:opacity-40 disabled:cursor-not-allowed
                min-h-[44px] dark:text-ink
              "
            >
              {t('photo.add')}
            </button>
            <button
              type="submit"
              disabled={logMutation.isPending || inputText.trim().length === 0}
              className="
                rounded-lg px-5 py-2 text-sm font-medium
                bg-leaf text-white hover:bg-leaf-deep
                focus:outline-none focus:ring-2 focus:ring-leaf
                disabled:opacity-40 disabled:cursor-not-allowed
                min-h-[44px]
              "
            >
              {logMutation.isPending ? t('loading') : t('button')}
            </button>
          </div>
        </form>

        {imageError && (
          <p role="alert" className="text-sm text-clay dark:text-clay">
            {imageError}
          </p>
        )}

        {/* Photo confirmation — preview + meal/label toggle before sending. */}
        {pendingImage && (
          <div className="flex flex-col gap-3 rounded-lg border border-ink/20 p-3 dark:border-ink/20">
            <img
              src={pendingImage.dataUrl}
              alt={t('photo.hint')}
              className="max-h-64 w-full rounded-md object-contain"
            />
            <div className="flex gap-1 self-center rounded-full bg-husk/50 p-1">
              {(['meal', 'label'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setImageCapture(mode)}
                  aria-pressed={imageCapture === mode}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium ${
                    imageCapture === mode ? 'bg-leaf text-white' : 'text-ink dark:text-ink'
                  }`}
                >
                  {t(`photo.${mode}`)}
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingImage(null)}
                disabled={imageMutation.isPending}
                className="
                  rounded-lg border border-ink/20 px-4 py-2 text-sm font-medium text-ink
                  hover:bg-husk/40 disabled:opacity-40 min-h-[44px] dark:text-ink
                "
              >
                {t('photo.remove')}
              </button>
              <button
                type="button"
                onClick={() => imageMutation.mutate(pendingImage)}
                disabled={imageMutation.isPending}
                className="
                  rounded-lg bg-leaf px-5 py-2 text-sm font-medium text-white hover:bg-leaf-deep
                  disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]
                "
              >
                {imageMutation.isPending ? t('loading') : t('photo.send')}
              </button>
            </div>
          </div>
        )}

        {imageMutation.isError && (
          <p role="alert" aria-live="assertive" className="text-sm text-clay dark:text-clay">
            {t('error')}
          </p>
        )}

        {logMutation.isError && (
          <p role="alert" aria-live="assertive" className="text-sm text-clay dark:text-clay">
            {t('error')}
          </p>
        )}
      </div>

      {/* Quick log strip — favourites + recents */}
      <section aria-label={t('quickLog.title')} className="flex flex-col gap-2">
        <h3 className="text-xs font-medium uppercase tracking-wide text-ink-soft dark:text-ink-soft">
          {t('quickLog.title')}
        </h3>
        <QuickLogStrip onDayUpdate={setDay} />

        {/* Utility action row */}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setShowQuickAdd(true)}
            className="
              rounded-lg border border-husk px-3 py-1.5 text-xs font-medium text-ink
              hover:border-leaf hover:text-leaf
              focus:outline-none focus:ring-2 focus:ring-leaf
              dark:border-ink/20 dark:text-ink
            "
          >
            {t('quickLog.quickAdd')}
          </button>
          <button
            type="button"
            onClick={() => copyYesterdayMutation.mutate()}
            disabled={copyYesterdayMutation.isPending}
            className="
              rounded-lg border border-husk px-3 py-1.5 text-xs font-medium text-ink
              hover:border-leaf hover:text-leaf
              focus:outline-none focus:ring-2 focus:ring-leaf
              disabled:opacity-50
              dark:border-ink/20 dark:text-ink
            "
          >
            {copyYesterdayMutation.isPending ? '…' : t('quickLog.copyYesterday')}
          </button>
          {hasEntries && (
            <button
              type="button"
              onClick={() => setShowSaveMeal(true)}
              className="
                rounded-lg border border-husk px-3 py-1.5 text-xs font-medium text-ink
                hover:border-leaf hover:text-leaf
                focus:outline-none focus:ring-2 focus:ring-leaf
                dark:border-ink/20 dark:text-ink
              "
            >
              {t('quickLog.saveMeal')}
            </button>
          )}
        </div>

        {copyStatus && (
          <p className="text-xs text-ink-soft dark:text-ink-soft" role="status" aria-live="polite">
            {copyStatus}
          </p>
        )}
      </section>

      {/* Extracted chips */}
      {hasEntries && (
        <section aria-label="Extracted meal entries" className="flex flex-col gap-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-ink-soft dark:text-ink-soft">
            {t('entries.heading', { count: logResult.entries.length })}
          </h3>
          {logResult.entries
            .filter((e) => e.deletedAt === null)
            .map((entry) => (
              <EntryChip
                key={entry.id}
                entry={entry}
                onDayUpdate={handleDayUpdate}
                onDeleteRequest={handleDeleteRequest}
                onMergeRequest={handleMergeRequest}
              />
            ))}
        </section>
      )}

      {/* Clarification rows */}
      {hasClarifications && logResult.rawLogId && (
        <section aria-label="Clarifications needed" className="flex flex-col gap-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-ink-soft dark:text-ink-soft">
            {t('entries.needInput')}
          </h3>
          {logResult.clarifications.map((clarification) => (
            <DisambiguationRow
              key={clarification.entityName}
              rawLogId={logResult.rawLogId}
              clarification={clarification}
              onResolved={handleClarificationResolved}
            />
          ))}
        </section>
      )}

      {/* Today's log history — previous submissions for this day */}
      {dayEntriesData && dayEntriesData.groups.length > 0 && (
        <LogHistoryPanel
          groups={dayEntriesData.groups}
          date={today}
          onDayUpdate={(updatedDay) => setDay(updatedDay)}
          onNewLog={(result) => {
            setLogResult(result);
            setDay(result.day);
            void qc.invalidateQueries({ queryKey: DAY_ENTRIES_KEY(today) });
          }}
        />
      )}

      {/* Undo toast */}
      {undoItem && (
        <UndoToast
          message={undoItem.label}
          onUndo={handleUndo}
          onDismiss={handleUndoToastDismiss}
        />
      )}

      <QuickAddSheet
        open={showQuickAdd}
        date={today}
        onClose={() => setShowQuickAdd(false)}
        onDayUpdate={setDay}
      />

      <SaveMealDialog
        open={showSaveMeal}
        entryIds={logResult?.entries.filter((e) => e.deletedAt === null).map((e) => e.id) ?? []}
        onClose={() => setShowSaveMeal(false)}
        onSaved={() => {}}
      />
    </section>
  );
}
